import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { pickProductMediaUrl } from '../utils/media';
import { useCmsMedia } from '../hooks/useCmsMedia';

interface InventoryProduct {
  id: string;
  name?: string;
  category?: string;
  collection?: string;
  stockCount?: number;
  inStock?: boolean;
  madeToOrder?: boolean;
  inventoryRecordId?: string;
  hasVariants?: boolean;
  variants?: Array<{
    id: string;
    name: string;
    stockCount?: number;
    inStock?: boolean;
    madeToOrder?: boolean;
    color?: string;
    material?: string;
    images?: string[];
  }>;
}

interface MadeToOrderState {
  [key: string]: {
    saving: boolean;
    error: string | null;
  };
}

export function CMSInventory() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { getMediaByProduct, loading: mediaLoading } = useCmsMedia();
  const [products, setProducts] = useState<InventoryProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStockStatus, setFilterStockStatus] = useState<'all' | 'in-stock' | 'out-of-stock' | 'low-stock' | 'made-to-order'>('all');
  const [expandedProducts, setExpandedProducts] = useState<Set<string> | 'all'>('all');
  const [madeToOrderState, setMadeToOrderState] = useState<MadeToOrderState>({});

  // Auto-save status per item: 'saving' | 'saved' | 'error'
  const [savingItems, setSavingItems] = useState<Record<string, 'saving' | 'saved' | 'error'>>({});
  // Which item is being edited inline (user tapped the number)
  const [editingItem, setEditingItem] = useState<string | null>(null);

  // Fetch products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/cms/products', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Failed to fetch products');
        }
        setProducts(data.products || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [token]);

  // Filter options from products
  const filterOptions = useMemo(() => {
    const categories = Array.from(new Set(products.map((p) => p.category).filter(Boolean))) as string[];
    return {
      categories: categories.sort((a, b) => a.localeCompare(b)),
    };
  }, [products]);

  // Filter products
  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products.filter((product) => {
      if (filterCategory !== 'all' && product.category !== filterCategory) return false;

      const totalStock = product.stockCount || 0;
      const isMadeToOrder = product.madeToOrder || false;
      if (filterStockStatus === 'in-stock' && totalStock <= 0) return false;
      if (filterStockStatus === 'out-of-stock' && (totalStock > 0 || isMadeToOrder)) return false;
      if (filterStockStatus === 'low-stock' && totalStock > 5) return false;
      if (filterStockStatus === 'made-to-order' && !isMadeToOrder) return false;

      if (!term) return true;
      return [product.id, product.name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [products, search, filterCategory, filterStockStatus]);

  // Stock statistics
  const stockStats = useMemo(() => {
    const totalProducts = products.length;
    const productsInStock = products.filter((p) => (p.stockCount || 0) > 0).length;
    const productsOutOfStock = products.filter((p) => (p.stockCount || 0) === 0 && !p.madeToOrder).length;
    const madeToOrderProducts = products.filter((p) => p.madeToOrder).length;
    const lowStockProducts = products.filter((p) => (p.stockCount || 0) > 0 && (p.stockCount || 0) <= 5).length;
    const totalUnits = products.reduce((sum, p) => sum + (p.stockCount || 0), 0);
    return { totalProducts, productsInStock, productsOutOfStock, madeToOrderProducts, lowStockProducts, totalUnits };
  }, [products]);

  // Resolve product image URL
  const resolveImageUrl = (product: InventoryProduct): string => {
    if (mediaLoading) return '';
    const productMedia = getMediaByProduct(product.id || '');
    if (productMedia.length > 0) {
      const mainImageUrl = pickProductMediaUrl(productMedia, ['product-main', '-main', 'main'], 'product-main');
      if (mainImageUrl) return mainImageUrl;
    }
    if (product.variants && product.variants.length > 0) {
      const firstVariant = product.variants[0];
      if (firstVariant.images && firstVariant.images.length > 0 && firstVariant.images[0]) {
        return firstVariant.images[0];
      }
    }
    return '';
  };

  // Toggle product expansion (for variants)
  const isExpanded = (productId: string) =>
    expandedProducts === 'all' || expandedProducts.has(productId);

  const toggleExpand = (productId: string) => {
    setExpandedProducts((prev) => {
      if (prev === 'all') {
        // First toggle: collapse this one, expand all others
        const all = new Set(products.filter(p => p.variants && p.variants.length > 0).map(p => p.id));
        all.delete(productId);
        return all;
      }
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  // ─── Auto-save stock adjustment ───────────────────────────────────────────

  const updateLocalStock = (itemId: string, newCount: number) => {
    setProducts((prev) =>
      prev.map((product) => {
        if (product.id === itemId) {
          return { ...product, stockCount: newCount, inStock: newCount > 0 };
        }
        if (product.variants?.some((v) => v.id === itemId)) {
          const updatedVariants = product.variants.map((v) =>
            v.id === itemId ? { ...v, stockCount: newCount, inStock: newCount > 0 } : v
          );
          const totalStock = updatedVariants.reduce((sum, v) => sum + (v.stockCount || 0), 0);
          return { ...product, variants: updatedVariants, stockCount: totalStock, inStock: totalStock > 0 };
        }
        return product;
      })
    );
  };

  const getCurrentStock = (itemId: string): number => {
    for (const p of products) {
      if (p.id === itemId) return p.stockCount || 0;
      if (p.variants) {
        const v = p.variants.find((v) => v.id === itemId);
        if (v) return v.stockCount || 0;
      }
    }
    return 0;
  };

  const saveStock = async (itemId: string, inventoryId: string, newCount: number, previousCount: number) => {
    setSavingItems((prev) => ({ ...prev, [itemId]: 'saving' }));
    try {
      const res = await fetch(`/api/cms/inventory?id=${encodeURIComponent(inventoryId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stockCount: newCount }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update');
      }
      setSavingItems((prev) => ({ ...prev, [itemId]: 'saved' }));
      setTimeout(() => setSavingItems((prev) => { const n = { ...prev }; delete n[itemId]; return n; }), 1200);
    } catch {
      updateLocalStock(itemId, previousCount);
      setSavingItems((prev) => ({ ...prev, [itemId]: 'error' }));
      setTimeout(() => setSavingItems((prev) => { const n = { ...prev }; delete n[itemId]; return n; }), 2000);
    }
  };

  const adjustStock = (itemId: string, inventoryId: string, delta: number) => {
    const current = getCurrentStock(itemId);
    const newCount = Math.max(0, current + delta);
    if (newCount === current) return;
    updateLocalStock(itemId, newCount);
    saveStock(itemId, inventoryId, newCount, current);
  };

  const setExactStock = (itemId: string, inventoryId: string, value: number) => {
    const current = getCurrentStock(itemId);
    const newCount = Math.max(0, value);
    if (newCount === current) return;
    updateLocalStock(itemId, newCount);
    saveStock(itemId, inventoryId, newCount, current);
  };

  // ─── Made to Order toggle ─────────────────────────────────────────────────

  const toggleMadeToOrder = async (itemId: string, inventoryId: string, currentValue: boolean) => {
    if (!token) return;
    setMadeToOrderState((prev) => ({ ...prev, [itemId]: { saving: true, error: null } }));
    try {
      const res = await fetch(`/api/cms/inventory?id=${encodeURIComponent(inventoryId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ madeToOrder: !currentValue }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update');
      }
      setProducts((prev) =>
        prev.map((product) => {
          if (product.id === itemId) return { ...product, madeToOrder: !currentValue };
          if (product.variants?.some((v) => v.id === itemId)) {
            return { ...product, variants: product.variants.map((v) => v.id === itemId ? { ...v, madeToOrder: !currentValue } : v) };
          }
          return product;
        })
      );
      setMadeToOrderState((prev) => ({ ...prev, [itemId]: { saving: false, error: null } }));
    } catch (err) {
      setMadeToOrderState((prev) => ({ ...prev, [itemId]: { saving: false, error: err instanceof Error ? err.message : 'Failed' } }));
    }
  };

  // ─── Stock badge ──────────────────────────────────────────────────────────

  const getStockBadge = (stockCount: number, madeToOrder?: boolean) => {
    if (stockCount > 0) {
      if (stockCount <= 5) {
        return <span className="px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400 rounded text-[11px] font-medium whitespace-nowrap">Low</span>;
      }
      return <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 rounded text-[11px] font-medium whitespace-nowrap">{stockCount} in stock</span>;
    }
    if (madeToOrder) {
      return <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400 rounded text-[11px] font-medium whitespace-nowrap">MTO</span>;
    }
    return <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded text-[11px] font-medium whitespace-nowrap">Out</span>;
  };

  return (
    <div className="min-h-screen bg-brand-sand dark:bg-brand-dark overflow-x-hidden">
      <header className="bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md shadow-sm border-b border-black/5 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <button
                onClick={() => navigate('/cms/dashboard')}
                className="text-sm text-brand-gray dark:text-white/60 hover:text-brand-dark dark:hover:text-white"
              >
                ← Dashboard
              </button>
              <span className="text-black/20 dark:text-white/20">|</span>
              <a href="/" className="text-sm text-brand-gray dark:text-white/60 hover:text-brand-dark dark:hover:text-white">
                View Store
              </a>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-brand-dark dark:text-white">Inventory</h1>
            <p className="text-brand-gray dark:text-white/60 mt-1">Quick stock management</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => navigate('/cms/products')}
              className="px-4 py-2 border border-black/15 dark:border-white/15 text-brand-gray dark:text-white/60 rounded-lg text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            >
              Edit Products
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {/* Stock Stats */}
        <section className="bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md rounded-xl shadow-sm border border-black/5 dark:border-white/10 p-4 overflow-x-auto">
          <div className="flex gap-6 text-sm text-brand-dark dark:text-white/60 min-w-max">
            <div>
              <div className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide">Total</div>
              <div className="text-lg font-semibold text-brand-dark dark:text-white">{stockStats.totalProducts}</div>
            </div>
            <div>
              <div className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide">In Stock</div>
              <div className="text-lg font-semibold text-green-600 dark:text-green-500">{stockStats.productsInStock}</div>
            </div>
            <div>
              <div className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide">MTO</div>
              <div className="text-lg font-semibold text-amber-600 dark:text-amber-500">{stockStats.madeToOrderProducts}</div>
            </div>
            <div>
              <div className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide">Out</div>
              <div className="text-lg font-semibold text-red-600 dark:text-red-500">{stockStats.productsOutOfStock}</div>
            </div>
            <div>
              <div className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide">Low (≤5)</div>
              <div className="text-lg font-semibold text-yellow-600 dark:text-yellow-500">{stockStats.lowStockProducts}</div>
            </div>
            <div>
              <div className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide">Units</div>
              <div className="text-lg font-semibold text-brand-dark dark:text-white">{stockStats.totalUnits}</div>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md rounded-xl shadow-sm border border-black/5 dark:border-white/10 p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-0 md:min-w-[200px]">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products..."
                className="w-full px-3 py-2.5 border border-black/15 dark:border-white/15 dark:bg-white/5 dark:text-white rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-brand-blue text-sm"
              />
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2.5 border border-black/15 dark:border-white/15 dark:bg-white/5 dark:text-white rounded-lg text-sm"
            >
              <option value="all">All Categories</option>
              {filterOptions.categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <select
              value={filterStockStatus}
              onChange={(e) => setFilterStockStatus(e.target.value as typeof filterStockStatus)}
              className="px-3 py-2.5 border border-black/15 dark:border-white/15 dark:bg-white/5 dark:text-white rounded-lg text-sm"
            >
              <option value="all">All Status</option>
              <option value="in-stock">In Stock</option>
              <option value="made-to-order">Made to Order</option>
              <option value="out-of-stock">Out of Stock</option>
              <option value="low-stock">Low Stock</option>
            </select>
          </div>
        </section>

        {/* Inventory List */}
        <section className="bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md rounded-xl shadow-sm border border-black/5 dark:border-white/10">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3 text-brand-gray dark:text-white/60">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Loading inventory...
              </div>
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-600 dark:text-red-400">{error}</div>
          ) : filteredProducts.length === 0 ? (
            <div className="p-6 text-center text-brand-gray dark:text-white/60">
              No products match your filters.
            </div>
          ) : (
            <div className="divide-y divide-black/5 dark:divide-white/10">
              {filteredProducts.map((product) => {
                const hasVariants = product.variants && product.variants.length > 0;
                const expanded = isExpanded(product.id);

                return (
                  <div key={product.id}>
                    {/* Product Row */}
                    <div className="p-4 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors">
                      <div className="flex items-center gap-3">
                        {/* Expand chevron or spacer */}
                        {hasVariants ? (
                          <button
                            onClick={() => toggleExpand(product.id)}
                            className="p-1.5 -ml-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors flex-shrink-0"
                          >
                            <svg
                              className={`w-4 h-4 text-brand-gray transition-transform ${expanded ? 'rotate-90' : ''}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        ) : (
                          <div className="w-0 md:w-7 flex-shrink-0" />
                        )}

                        {/* Thumbnail */}
                        <div className="w-10 h-10 rounded-lg bg-black/5 dark:bg-white/5 flex-shrink-0 overflow-hidden">
                          {resolveImageUrl(product) ? (
                            <img src={resolveImageUrl(product)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-brand-gray/40 text-sm">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-brand-dark dark:text-white text-sm truncate">
                            {product.name || product.id}
                          </div>
                          <div className="text-xs text-brand-gray dark:text-white/50 flex items-center gap-1.5 mt-0.5 flex-wrap">
                            {product.category && <span>{product.category}</span>}
                            {product.category === 'Rings' && (
                              <span className="text-[10px] px-1 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded" title="Ring sizes managed in code">sizes in code</span>
                            )}
                            {hasVariants && (
                              <span className="text-brand-blue">{product.variants!.length}v</span>
                            )}
                          </div>
                        </div>

                        {/* Badge - hidden on mobile, shown on md+ */}
                        <div className="hidden md:block flex-shrink-0">
                          {getStockBadge(product.stockCount || 0, product.madeToOrder)}
                        </div>

                        {/* Stepper - shown on md+ inline */}
                        {!hasVariants && (
                          <div className="hidden md:flex items-center flex-shrink-0">
                            <StockStepper
                              itemId={product.id}
                              inventoryId={product.inventoryRecordId || product.id}
                              stockCount={product.stockCount || 0}
                              savingStatus={savingItems[product.id]}
                              editingItem={editingItem}
                              setEditingItem={setEditingItem}
                              onAdjust={adjustStock}
                              onSetExact={setExactStock}
                            />
                          </div>
                        )}

                        {/* MTO toggle - shown on md+ inline */}
                        {!hasVariants && (
                          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
                            <MtoToggle
                              itemId={product.id}
                              inventoryId={product.inventoryRecordId || product.id}
                              madeToOrder={product.madeToOrder || false}
                              saving={madeToOrderState[product.id]?.saving}
                              onToggle={toggleMadeToOrder}
                            />
                          </div>
                        )}
                      </div>

                      {/* Mobile second row: badge + stepper + MTO */}
                      {!hasVariants && (
                        <div className="flex items-center gap-2 mt-2.5 ml-[43px] md:hidden">
                          {getStockBadge(product.stockCount || 0, product.madeToOrder)}
                          <div className="flex-1" />
                          <StockStepper
                            itemId={product.id}
                            inventoryId={product.inventoryRecordId || product.id}
                            stockCount={product.stockCount || 0}
                            savingStatus={savingItems[product.id]}
                            editingItem={editingItem}
                            setEditingItem={setEditingItem}
                            onAdjust={adjustStock}
                            onSetExact={setExactStock}
                          />
                          <MtoToggle
                            itemId={product.id}
                            inventoryId={product.inventoryRecordId || product.id}
                            madeToOrder={product.madeToOrder || false}
                            saving={madeToOrderState[product.id]?.saving}
                            onToggle={toggleMadeToOrder}
                          />
                        </div>
                      )}

                      {/* Mobile badge row for products WITH variants (no stepper on parent) */}
                      {hasVariants && (
                        <div className="flex items-center gap-2 mt-2 ml-[43px] md:hidden">
                          {getStockBadge(product.stockCount || 0, product.madeToOrder)}
                        </div>
                      )}
                    </div>

                    {/* Variant Rows */}
                    {hasVariants && expanded && (
                      <div className="bg-black/[0.02] dark:bg-white/[0.02] border-t border-black/5 dark:border-white/5">
                        {product.variants!.map((variant) => (
                          <div
                            key={variant.id}
                            className="px-4 py-3 pl-8 md:pl-16 border-b border-black/5 dark:border-white/5 last:border-b-0"
                          >
                            <div className="flex items-center gap-2 md:gap-3">
                              {/* Variant info */}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-brand-dark dark:text-white text-sm truncate">
                                  {variant.name || variant.color || variant.material || variant.id}
                                </div>
                                {variant.color && variant.name && (
                                  <div className="text-xs text-brand-gray dark:text-white/50 mt-0.5 hidden md:block">
                                    {variant.color}
                                  </div>
                                )}
                              </div>

                              {/* Badge */}
                              <div className="flex-shrink-0 hidden md:block">
                                {getStockBadge(variant.stockCount || 0, variant.madeToOrder)}
                              </div>

                              {/* Stepper — always visible */}
                              <div className="flex items-center flex-shrink-0">
                                <StockStepper
                                  itemId={variant.id}
                                  inventoryId={variant.id}
                                  stockCount={variant.stockCount || 0}
                                  savingStatus={savingItems[variant.id]}
                                  editingItem={editingItem}
                                  setEditingItem={setEditingItem}
                                  onAdjust={adjustStock}
                                  onSetExact={setExactStock}
                                />
                              </div>

                              {/* MTO — always visible */}
                              <div className="flex items-center flex-shrink-0">
                                <MtoToggle
                                  itemId={variant.id}
                                  inventoryId={variant.id}
                                  madeToOrder={variant.madeToOrder || false}
                                  saving={madeToOrderState[variant.id]?.saving}
                                  onToggle={toggleMadeToOrder}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

// ─── Stock Stepper ────────────────────────────────────────────────────────────

function StockStepper({
  itemId,
  inventoryId,
  stockCount,
  savingStatus,
  editingItem,
  setEditingItem,
  onAdjust,
  onSetExact,
}: {
  itemId: string;
  inventoryId: string;
  stockCount: number;
  savingStatus?: 'saving' | 'saved' | 'error';
  editingItem: string | null;
  setEditingItem: (id: string | null) => void;
  onAdjust: (itemId: string, inventoryId: string, delta: number) => void;
  onSetExact: (itemId: string, inventoryId: string, value: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isEditing = editingItem === itemId;

  const handleStartEdit = () => {
    setEditingItem(itemId);
    setTimeout(() => {
      inputRef.current?.select();
    }, 50);
  };

  const handleFinishEdit = () => {
    if (!inputRef.current) return;
    const val = Math.max(0, parseInt(inputRef.current.value) || 0);
    onSetExact(itemId, inventoryId, val);
    setEditingItem(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleFinishEdit();
    if (e.key === 'Escape') setEditingItem(null);
  };

  const countColor = savingStatus === 'saved'
    ? 'text-green-600 dark:text-green-400'
    : savingStatus === 'error'
    ? 'text-red-600 dark:text-red-400'
    : 'text-brand-dark dark:text-white';

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onAdjust(itemId, inventoryId, -1)}
        disabled={stockCount <= 0}
        className="w-8 h-8 flex items-center justify-center rounded-lg border border-black/10 dark:border-white/10 text-brand-gray dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors active:scale-95"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
        </svg>
      </button>

      {isEditing ? (
        <input
          ref={inputRef}
          type="number"
          min="0"
          defaultValue={stockCount}
          onBlur={handleFinishEdit}
          onKeyDown={handleKeyDown}
          className="w-12 h-8 text-center text-sm font-semibold border border-brand-blue dark:border-brand-blue rounded-lg bg-white dark:bg-brand-dark text-brand-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          autoFocus
        />
      ) : (
        <button
          onClick={handleStartEdit}
          className={`w-10 h-8 flex items-center justify-center text-sm font-semibold rounded-lg transition-colors ${countColor} ${
            savingStatus === 'saving' ? 'animate-pulse' : ''
          } hover:bg-black/5 dark:hover:bg-white/5`}
          title="Click to type exact value"
        >
          {stockCount}
        </button>
      )}

      <button
        onClick={() => onAdjust(itemId, inventoryId, 1)}
        className="w-8 h-8 flex items-center justify-center rounded-lg border border-black/10 dark:border-white/10 text-brand-gray dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5 transition-colors active:scale-95"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>
  );
}

// ─── Made-to-Order Toggle ─────────────────────────────────────────────────────

function MtoToggle({
  itemId,
  inventoryId,
  madeToOrder,
  saving,
  onToggle,
}: {
  itemId: string;
  inventoryId: string;
  madeToOrder: boolean;
  saving?: boolean;
  onToggle: (itemId: string, inventoryId: string, currentValue: boolean) => void;
}) {
  return (
    <button
      onClick={() => onToggle(itemId, inventoryId, madeToOrder)}
      disabled={saving}
      className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
        madeToOrder ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
      } ${saving ? 'opacity-50' : ''}`}
      title={madeToOrder ? 'Made to Order: ON' : 'Made to Order: OFF'}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
          madeToOrder ? 'translate-x-5' : ''
        }`}
      />
    </button>
  );
}
